import React, {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  FC,
  ReactNode,
  useRef,
} from 'react';
import {
  IUser,
  UserContextType,
  UserAction,
  ProfilePrefsType,
} from '../@types/user';
import { useMutation } from '@apollo/client';
import { LOG_OUT } from '../apollo/mutations';
import { getSystemTimezone } from '../components/Calendar/utils';

const systemTimezone = getSystemTimezone();
const browserTz: string = new Intl.DateTimeFormat('en-US').resolvedOptions()
  .timeZone;

const initialState: IUser = {
  isLoggedIn: false,
  isVerified: false,
  isStaff: false,
  isHost: false,
  initial: '',
  theme: 'light',
  timezone: 'America/Chicago', // timezone
  live: [], // active live event ids
  fullScreen: false,
};
const defaultTz = browserTz || systemTimezone || 'America/Chicago';

export const UserContext = createContext<UserContextType>({
  state: initialState,
  dispatch: () => null,
  signOut: () => Promise.resolve(),
  signOutWithError: () => null,
  logIn: () => Promise.resolve(),
  setTheme: () => null,
  setPrefs: () => null,
  setMessage: () => null,
  setLiveEvents: () => null,
  toggleFullScreen: () => null,
});

export const userReducer = (state: IUser, action: UserAction): IUser => {
  switch (action.type) {
    case 'LOG_IN':
      return {
        ...state,
        isLoggedIn: true,
        isVerified: action.payload.isVerified,
        initial: action.payload.initial,
        isStaff: action.payload.isStaff,
        isHost: action.payload.isHost,
        theme: action.payload.theme,
        timezone: action.payload.timezone || state.timezone,
      };
    case 'LOG_OUT':
      return {
        ...state,
        isLoggedIn: false,
        isVerified: false,
        initial: '',
        isStaff: false,
        isHost: false,
        theme: '',
        timezone: defaultTz, // reset to default timezone
      };
    case 'VERIFY':
      return {
        ...state,
        isLoggedIn: true,
        isVerified: false,
        initial: action.payload,
      };
    case 'SET_VERIFICATION':
      return {
        ...state,
        isVerified: true,
      };
    case 'SET_THEME':
      return {
        ...state,
        theme: action.payload,
      };
    case 'SET_TIMEZONE':
      return {
        ...state,
        timezone: action.payload,
      };
    case 'SET_MESSAGE':
      return {
        ...state,
        message: action.payload,
      };
    case 'SET_LIVE_EVENTS':
      return {
        ...state,
        live: action.payload,
      };
    case 'TOGGLE_FULL_SCREEN':
      return {
        ...state,
        fullScreen: !state.fullScreen,
      };
    default:
      return state;
  }
};

const SessionTimeoutProvider: FC<{
  children: ReactNode;
  isLoggedIn: boolean;
  signOut: () => void;
  resetTime: number;
}> = ({ children, isLoggedIn, signOut, resetTime }) => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const events = ['mousedown', 'wheel', 'keydown', 'touchstart', 'scroll'];

  useEffect(() => {
    if (!isLoggedIn) return;

    const handleWindowEvents = () => {
      clearTimeout(timeoutRef.current as NodeJS.Timeout);
      timeoutRef.current = setTimeout(() => {
        signOut();
      }, resetTime);
    };

    events.forEach(event => window.addEventListener(event, handleWindowEvents));
    handleWindowEvents();

    return () => {
      events.forEach(event =>
        window.removeEventListener(event, handleWindowEvents),
      );
      clearTimeout(timeoutRef.current as NodeJS.Timeout);
    };
  }, [isLoggedIn, signOut, resetTime]);

  return <>{children}</>;
};

const UserProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const isBrowser = () => typeof window !== 'undefined';
  const storedState = isBrowser() && localStorage.getItem('cs-ux');

  const getEnv = (env: string | undefined) => {
    const val = env && env;
    return val;
  };

  const cookieCodes = {
    isHost: getEnv(process.env.GATSBY_IS_HOST),
    isLoggedIn: getEnv(process.env.GATSBY_IS_LOGGED_IN),
    isStaff: getEnv(process.env.GATSBY_IS_STAFF),
    isVerified: getEnv(process.env.GATSBY_IS_VERIFIED),
    fullScreen: getEnv(process.env.GATSBY_FULL_SCREEN),
  };

  const getStateDecoder = (storedCodes: string) => {
    const ps: string = JSON.parse(storedCodes);
    const carrotIndex: number = ps.indexOf('^');
    const atIndex: number = ps.indexOf('@');
    const hashIndex: number = ps.indexOf('#');
    const stateCodes = ps.slice(0, carrotIndex) || '';
    const initial = ps.slice(carrotIndex + 1, atIndex) || '';
    const theme: string = ps.slice(atIndex + 1, hashIndex) || 'light';
    const tzPref: string = ps.slice(hashIndex + 1) || defaultTz;

    let decodedState: any = {};
    if (stateCodes && stateCodes.length > 0) {
      for (const [key, value] of Object.entries(cookieCodes)) {
        let keyName = key;
        if (value && stateCodes.includes(value)) {
          decodedState[keyName] = true;
        } else {
          decodedState[keyName] = false;
        }
      }
    } else {
      const returnState = {
        ...initialState,
        initial: initial,
        theme: theme,
        timezone: tzPref,
      };
      return {
        returnState,
      };
    }
    const fullDecoded = {
      ...decodedState,
      initial: initial,
      theme: theme,
      timezone: tzPref,
    };
    return fullDecoded;
  };

  const getCookieCodes = (data: any) => {
    let codes: string = '';
    for (const [key, value] of Object.entries(data)) {
      if (value === true) {
        const stateKey = key;
        for (const [key, value] of Object.entries(cookieCodes)) {
          if (key === stateKey) {
            codes = codes + value;
          }
        }
      }
    }
    return codes;
  };

  const defaultState: IUser = storedState
    ? getStateDecoder(storedState)
    : initialState;

  const localState: IUser = { ...defaultState };

  const [state, dispatch] = useReducer(userReducer, localState || initialState);

  useEffect(() => {
    handleCodeLocalStorage(state);
  }, [state]);

  const handleCodeLocalStorage = (state: IUser) => {
    const varState = {
      isLoggedIn: state.isLoggedIn,
      isVerified: state.isVerified,
      isStaff: state.isStaff,
      isHost: state.isHost,
      fullScreen: state.fullScreen,
    };
    const regState = {
      initial: state.initial || '',
      theme: state.theme || 'light',
      timezone: state.timezone || browserTz || systemTimezone,
    };
    const codedState = getCookieCodes(varState) || '';
    isBrowser() &&
      localStorage.setItem(
        'cs-ux',
        JSON.stringify(
          codedState +
            '^' +
            regState.initial +
            '@' +
            regState.theme +
            '#' +
            regState.timezone,
        ),
      );
  };

  const [signOutMutation, { client, loading, error }] = useMutation(LOG_OUT);

  const signOut = useCallback(
    async (message?: string) => {
      dispatch({ type: 'LOG_OUT' });
      try {
        await signOutMutation();
      } catch (e) {
        console.error('signOut mutation failed', e);
      } finally {
        dispatch({ type: 'SET_MESSAGE', payload: message || 'You have signed out' });
        try {
          await client?.clearStore();
        } catch (e) {
          console.error('clearStore failed', e);
        }
      }
    },
    [signOutMutation, client],
  );

  // TODO send error report to server log
  const signOutWithError = (error: string) => {
    signOut(error);
  };

  const logIn = async (props: IUser) => {
    // Clear before flipping isLoggedIn so nothing can mount and query against
    // a cache still holding the previous session's user. Identity-scoped
    // queries (me, sbuUser, person, etc.) are unkeyed, so a stale entry would
    // otherwise be served to the newly logged-in user under cache-first.
    try {
      await client?.clearStore();
    } catch (e) {
      console.error('clearStore failed', e);
    }
    handleCodeLocalStorage({
      ...props,
    });
    dispatch({ type: 'LOG_IN', payload: props });
  };

  const setTheme = (props: IUser['theme']) => {
    dispatch({ type: 'SET_THEME', payload: props });
  };
  const setMessage = (message: string) => {
    dispatch({ type: 'SET_MESSAGE', payload: message });
  };

  const setLiveEvents = (live: string[]) => {
    dispatch({ type: 'SET_LIVE_EVENTS', payload: live });
  };
  const toggleFullScreen = () => {
    dispatch({ type: 'TOGGLE_FULL_SCREEN' });
  };
  const setPrefs = (props: ProfilePrefsType) => {
    handleCodeLocalStorage({
      ...state,
      theme: props.theme || state.theme,
      timezone: props.tzPref || state.timezone,
    });
    dispatch({ type: 'SET_THEME', payload: props.theme });
    dispatch({ type: 'SET_TIMEZONE', payload: props.tzPref });
  };
  const MINUTES_TO_RESET =
    Number(getEnv(process.env.GATSBY_MINUTES_TO_RESET)) ?? 60;
  const resetTime: number = 60 * 1000 * MINUTES_TO_RESET;

  return (
    <UserContext.Provider
      value={{
        state,
        dispatch,
        signOut,
        signOutWithError,
        logIn,
        setTheme,
        setPrefs,
        setMessage,
        setLiveEvents,
        toggleFullScreen,
      }}
    >
      <SessionTimeoutProvider
        isLoggedIn={state.isLoggedIn}
        signOut={signOut}
        resetTime={resetTime}
      >
        {children}
      </SessionTimeoutProvider>
    </UserContext.Provider>
  );
};

export default UserProvider;
